/**
 * Data layer for `aws-s3-export-scheduled-click-conversion-logs`.
 * Thin typed fetch wrappers over /api/v1/aws-s3-export-scheduled-click-conversion-logs.
 * Raw `fetch()` is confined to this file per the dash contract.
 *
 * Also exports pure helpers (parseCron) for unit testing without network.
 */

export type Cadence = "hourly" | "daily" | "weekly";
export type LogType = "clicks" | "conversions" | "both";
export type LastStatus = "pending" | "success" | "failed";

export interface DateRange {
	from?: string;
	to?: string;
	partition_by_date?: boolean;
	format?: "csv" | "ndjson" | "parquet";
	filter?: string;
}

export interface ScheduleRow {
	id: string;
	tenant_id: string;
	export_id: string;
	s3_bucket: string;
	s3_prefix: string;
	cadence: Cadence;
	log_type: LogType;
	date_range: DateRange;
	enabled: boolean;
	next_run_at: number | null;
	last_run_at: number | null;
	last_status: LastStatus | null;
	created_at: number;
	updated_at: number;
}

export interface ListResponse {
	items: ScheduleRow[];
}

export interface CreateBody {
	export_id: string;
	s3_bucket: string;
	s3_prefix?: string;
	cadence: Cadence;
	log_type: LogType;
	date_range?: DateRange;
	enabled?: boolean;
	next_run_at?: number | null;
}

export interface PatchBody {
	enabled?: boolean;
	cadence?: Cadence;
	last_status?: LastStatus;
	last_run_at?: number;
	next_run_at?: number | null;
}

const BASE = "/api/v1/aws-s3-export-scheduled-click-conversion-logs";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListResponse>(res);
}

export async function create(body: CreateBody): Promise<{ item: ScheduleRow }> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ item: ScheduleRow }>(res);
}

export async function patchSchedule(
	id: string,
	body: PatchBody,
): Promise<{ item: ScheduleRow }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "PATCH",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ item: ScheduleRow }>(res);
}

export async function runNow(id: string): Promise<{ item: ScheduleRow }> {
	// "Run now" is modeled as a PATCH that stamps next_run_at to now.
	const now = Math.floor(Date.now() / 1000);
	return patchSchedule(id, { next_run_at: now, last_status: "pending" });
}

export async function remove(id: string): Promise<{ ok: boolean; id: string }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ ok: boolean; id: string }>(res);
}

export const awsS3ExportScheduledClickConversionLogsQueryKeys = {
	all: ["aws-s3-export-scheduled-click-conversion-logs"] as const,
	list: () => [...awsS3ExportScheduledClickConversionLogsQueryKeys.all] as const,
	detail: (id: string) =>
		[...awsS3ExportScheduledClickConversionLogsQueryKeys.all, id] as const,
};

// ---------------------------------------------------------------------------
// Pure helpers (network-free, unit-testable)
// ---------------------------------------------------------------------------

export interface ParsedCron {
	ok: boolean;
	cadence: Cadence | null;
	minutes: number | null;
	hours: number | null;
	reason?: string;
}

/**
 * Parse a simplified cron expression into a cadence + time hints.
 * Accepts:
 *   "0 * * * *"       → hourly at :00
 *   "@hourly"         → hourly
 *   "0 9 * * *"       → daily at 09:00
 *   "@daily"          → daily
 *   "0 9 * * 1"       → weekly Monday 09:00
 *   "@weekly"         → weekly
 * Anything else → { ok:false, reason:... }
 */
export function parseCron(expr: string): ParsedCron {
	const trimmed = (expr ?? "").trim().toLowerCase();
	if (!trimmed) {
		return { ok: false, cadence: null, minutes: null, hours: null, reason: "empty" };
	}
	if (trimmed === "@hourly") {
		return { ok: true, cadence: "hourly", minutes: 0, hours: null };
	}
	if (trimmed === "@daily" || trimmed === "@midnight") {
		return { ok: true, cadence: "daily", minutes: 0, hours: 0 };
	}
	if (trimmed === "@weekly") {
		return { ok: true, cadence: "weekly", minutes: 0, hours: 0 };
	}
	const parts = trimmed.split(/\s+/);
	if (parts.length !== 5) {
		return {
			ok: false,
			cadence: null,
			minutes: null,
			hours: null,
			reason: "expected 5 fields",
		};
	}
	const [minStr, hourStr, dom, mon, dow] = parts;
	const minute = Number(minStr);
	const hour = Number(hourStr);
	if (!Number.isFinite(minute) || minute < 0 || minute > 59) {
		return {
			ok: false,
			cadence: null,
			minutes: null,
			hours: null,
			reason: "bad minute",
		};
	}
	if (hourStr === "*" && minStr !== "*") {
		return { ok: true, cadence: "hourly", minutes: minute, hours: null };
	}
	if (!Number.isFinite(hour) || hour < 0 || hour > 23) {
		if (hourStr !== "*") {
			return {
				ok: false,
				cadence: null,
				minutes: null,
				hours: null,
				reason: "bad hour",
			};
		}
	}
	const isStar = (v: string) => v === "*";
	if (isStar(dom) && isStar(mon) && isStar(dow)) {
		return { ok: true, cadence: "daily", minutes: minute, hours: hour };
	}
	if (isStar(dom) && isStar(mon) && !isStar(dow)) {
		const day = Number(dow);
		if (Number.isFinite(day) && day >= 0 && day <= 6) {
			return { ok: true, cadence: "weekly", minutes: minute, hours: hour };
		}
	}
	return {
		ok: false,
		cadence: null,
		minutes: null,
		hours: null,
		reason: "unsupported pattern",
	};
}

/**
 * Map a user-selected cadence back to a canonical cron string. Useful for
 * displaying the schedule next to a plain label.
 */
export function cadenceToCron(
	cadence: Cadence,
	hour = 0,
	minute = 0,
	dayOfWeek = 1,
): string {
	if (cadence === "hourly") return `${minute} * * * *`;
	if (cadence === "daily") return `${minute} ${hour} * * *`;
	return `${minute} ${hour} * * ${dayOfWeek}`;
}
